<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/table/PosTableFooter.vue.
  // Bulk actions reuse the exact logic/contracts of ModalPosTableFooter.vue:
  // printGroup, OrderGroupModal, massSetStatus, ModalPay and massMove.
  import { computed, getCurrentInstance, nextTick, ref } from 'vue';
  import {
    breakpointsTailwind,
    onClickOutside,
    useBreakpoints,
  } from '@vueuse/core';
  import { OrderPaymentStatus, OrderStatus } from '@/enums/OrderEnums';
  import { PaymentType } from '@/enums/PaymentEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { IOrder } from '@/interfaces/IOrder';
  import { ITable } from '@/interfaces/ITable';
  import { PrinterType } from '@/modules/PrintService';
  import { roundPrice } from '@/utils';
  import { getEffectiveTier } from '@/utils/billing-entitlements.utils';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import {
    isAutoInvoicePaymentMethod,
    isOrderInvoiceable,
    orderHasAutoInvoicePayments,
  } from '@/utils/orders.utils';
  import {
    IManualPrintGate,
    TManualPrintGateState,
  } from '@/utils/print-gate.utils';
  import OrdersService from '@/api/services/orders.service';
  import { useInvoiceAttempt } from '@/composition/use-invoice-attempt';
  import { useMassInvoice } from '@/composition/use-mass-invoice';
  import { useModal } from '@/composition/use-modal';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { deviceCanEverPrint, usePrinting } from '@/composition/use-printing';
  import { useTableDiscount } from '@/composition/use-table-discount';
  import { useUi } from '@/composition/use-ui';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import ModalPay from '@/components/modals/pay/ModalPay.vue';
  import OrderGroupModal from '@/components/modals/OrderGroupModal.vue';
  import SelectModal from '@/components/modals/SelectModal.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosV2TableDiscountChip from '@/components/pos-v2/table/PosV2TableDiscountChip.vue';

  const props = defineProps<{
    table: ITable;
    tableOrders: IOrder[];
    selectedOrders: IOrder[];
  }>();

  const modals = useModal();
  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const printing = usePrinting();
  const money = usePosMoney();
  const invoiceAttempt = useInvoiceAttempt();
  const { invoiceLoading, promptAndInvoice } = useMassInvoice();

  const { smaller } = useBreakpoints(breakpointsTailwind);
  const isCompact = smaller('lg');

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  //#region Totals / selection summaries

  const totalAmount = computed(() =>
    roundPrice(props.tableOrders.reduce((sum, o) => sum + o.total, 0))
  );

  // Table discount chip/flow (no chip without an open session — the server
  // has nothing to apply to).
  const {
    state: discountState,
    openSession: discountSession,
    saving: discountSaving,
    openModal: openDiscountModal,
  } = useTableDiscount(
    computed(() => props.tableOrders),
    computed(() => props.table?.name ?? null)
  );

  const selectedTotal = computed(() =>
    roundPrice(props.selectedOrders.reduce((sum, o) => sum + o.total, 0))
  );

  const selectedRemaining = computed(() =>
    roundPrice(
      props.selectedOrders.reduce(
        (sum, o) => sum + Math.max(0, o.total - o.totalPaid),
        0
      )
    )
  );

  const allSelectedFinished = computed(() => {
    if (!props.selectedOrders.length) return false;
    return props.selectedOrders.every((o) => o.status === OrderStatus.Finished);
  });

  const allSelectedPaid = computed(() => {
    if (!props.selectedOrders.length) return false;
    return props.selectedOrders.every(
      (o) => o.paymentStatus === OrderPaymentStatus.Paid
    );
  });

  //#endregion

  //#region Print dropdown (fixed positioning — same pattern as view actions)

  const printMenuRef = ref<HTMLElement>();
  const isPrintMenuOpen = ref(false);
  const printPanelStyle = ref<Record<string, string>>({});

  onClickOutside(printMenuRef, () => {
    isPrintMenuOpen.value = false;
  });

  function togglePrintMenu() {
    if (!isPrintMenuOpen.value) {
      const rect = printMenuRef.value?.getBoundingClientRect();
      if (rect) {
        printPanelStyle.value = {
          left: `${Math.max(8, rect.left)}px`,
          bottom: `${window.innerHeight - rect.top + 8}px`,
        };
      }
    }
    isPrintMenuOpen.value = !isPrintMenuOpen.value;
  }

  interface IPrintMenuItem {
    labelKey: string;
    icon: string;
    types: PrinterType[];
  }

  const printMenuItems: IPrintMenuItem[] = [
    {
      labelKey: 'posV2.printTicket',
      icon: 'ticket-percent',
      types: [PrinterType.Ticket],
    },
    {
      labelKey: 'posV2.printComanda',
      icon: 'receipt',
      types: [PrinterType.Comanda],
    },
    {
      labelKey: 'posV2.printEtiquetas',
      icon: 'tag-text',
      types: [PrinterType.Etiqueta],
    },
    {
      labelKey: 'posV2.printAll',
      icon: 'printer',
      types: [PrinterType.Ticket, PrinterType.Comanda, PrinterType.Etiqueta],
    },
  ];

  type TGatedPrinterType = PrinterType.Comanda | PrinterType.Etiqueta;

  function isGatedPrinterType(type: PrinterType): type is TGatedPrinterType {
    return type === PrinterType.Comanda || type === PrinterType.Etiqueta;
  }

  function reprintGroupMessageKey(types: TGatedPrinterType[]): string {
    const hasComanda = types.includes(PrinterType.Comanda);
    const hasEtiqueta = types.includes(PrinterType.Etiqueta);
    if (hasComanda && hasEtiqueta) {
      return 'posV2.reprintGroupComandaEtiquetasMsg';
    }
    return hasComanda
      ? 'posV2.reprintGroupComandaMsg'
      : 'posV2.reprintGroupEtiquetasMsg';
  }

  /** Any delta wins; a reprint dialog only fires for fully-printed selections. */
  function aggregateGateState(
    states: TManualPrintGateState[]
  ): TManualPrintGateState {
    if (states.some((state) => state === 'unprinted')) return 'unprinted';
    if (states.some((state) => state === 'reprint')) return 'reprint';
    return 'nothing';
  }

  async function handlePrint(item: IPrintMenuItem) {
    isPrintMenuOpen.value = false;

    const groupTitle = i18nT('posV2.tableOrdersPrintTitle', {
      table: props.table.name,
    });
    const what = i18nT(item.labelKey);

    // Snapshot the selection and compute every gate BEFORE any await: the
    // selection can change while the confirmation dialog is open.
    const orders = [...props.selectedOrders];

    const gatesByType = new Map<TGatedPrinterType, IManualPrintGate[]>();
    const aggregateByType = new Map<TGatedPrinterType, TManualPrintGateState>();
    for (const printerType of item.types) {
      if (!isGatedPrinterType(printerType)) continue;
      const gates = orders.map((order) =>
        printing.getManualPrintGate(order, printerType)
      );
      gatesByType.set(printerType, gates);
      aggregateByType.set(
        printerType,
        aggregateGateState(gates.map((gate) => gate.state))
      );
    }

    const typesNeedingConfirm = Array.from(aggregateByType.entries())
      .filter(([, state]) => state === 'reprint')
      .map(([type]) => type);

    let reprintConfirmed = false;
    if (typesNeedingConfirm.length > 0) {
      reprintConfirmed = await modals.confirm({
        title: i18nT('posV2.reprintTitle'),
        message: i18nT(reprintGroupMessageKey(typesNeedingConfirm)),
      });
    }

    const onPrintError = (error: unknown) => {
      ui.serverError(error, i18nT('posV2.printError', { what }));
    };

    for (const printerType of item.types) {
      // Ticket: ungated, identical to today.
      if (printerType === PrinterType.Ticket) {
        printing
          .printGroup(printerType, groupTitle, orders)
          .catch(onPrintError);
        continue;
      }

      const aggregate = aggregateByType.get(printerType)!;

      if (aggregate === 'nothing') {
        // Only-requested kind gets the toast; inside "Todo" skip silently.
        if (item.types.length === 1) {
          ui.showToast(AlertType.Info, i18nT('posV2.nothingToPrint'));
        }
        continue;
      }

      if (printerType === PrinterType.Comanda) {
        if (aggregate === 'unprinted') {
          printing
            .printGroup(printerType, groupTitle, orders)
            .catch(onPrintError);
        } else if (reprintConfirmed) {
          printing
            .printGroup(printerType, groupTitle, orders, undefined, {
              variant: 'reprint',
            })
            .catch(onPrintError);
        }
        continue;
      }

      // Etiqueta: per-order prints (the group sticker path ignores counters
      // and would print labels for canceled/unlabeled items on a reprint).
      // The confirmation only authorizes kinds that joined the dialog: in a
      // mixed selection (aggregate 'unprinted') fully-printed orders must be
      // skipped, not reprinted on the back of another kind's confirmation.
      const etiquetaConfirmed =
        reprintConfirmed && aggregate === 'reprint';
      const gates = gatesByType.get(printerType)!;
      orders.forEach((order, index) => {
        const gate = gates[index];
        if (gate.state === 'unprinted') {
          printing
            .printOrder({
              printerType: PrinterType.Etiqueta,
              order,
              printMode: 'unprinted',
            })
            .catch(onPrintError);
        } else if (gate.state === 'reprint' && etiquetaConfirmed) {
          printing
            .printOrder({
              printerType: PrinterType.Etiqueta,
              order,
              itemsToPrint: gate.reprintItems,
            })
            .catch(onPrintError);
        }
      });
    }
  }

  //#endregion

  //#region Bulk actions (contracts mirrored from ModalPosTableFooter)

  function openResume() {
    if (!props.selectedOrders.length) return;

    modals.show(
      OrderGroupModal,
      {
        title: i18nT('posV2.resumeTitle'),
        orders: props.selectedOrders,
        reloadItems: false,
      },
      {
        adaptive: true,
        height: 'auto',
        width: 550,
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
      }
    );
  }

  const settingStatus = ref(false);

  async function markDelivered() {
    if (!props.selectedOrders.length || settingStatus.value) return;

    settingStatus.value = true;
    try {
      // Selection is kept on purpose so the user can Pay right after.
      await OrdersService.massSetStatus(
        props.selectedOrders.map((o) => o.id),
        OrderStatus.Finished,
        restaurantStore.caja?.id
      );
    } catch (error) {
      ui.serverError(error, i18nT('posV2.statusUpdateError'));
    } finally {
      settingStatus.value = false;
    }
  }

  function doPayment() {
    const orders = props.selectedOrders.filter(
      (o) => o.paymentStatus !== OrderPaymentStatus.Paid
    );
    if (!orders.length) return;

    modals.show(
      ModalPay,
      {
        type: PaymentType.Order,
        unpaidAmount: roundPrice(
          orders.reduce(
            (acc, order) => acc + (order.total - order.totalPaid),
            0
          )
        ),
        entityIds: orders.map((o) => o.id),
      },
      {
        name: 'order-pay',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
      },
      {
        // Same auto-invoice gate as the single-order flow in
        // usePosOrderPayments::doPayment. Prefer session methods (no WS race);
        // fall back to store paymentsByType for money paid before the modal.
        paid: (
          totalPaid: number,
          remainingToPay: number,
          sessionPaymentMethods: PaymentMethod[] = []
        ) => {
          if (
            !restaurantStore.invoicingData?.data?.autoInvoice ||
            remainingToPay > 0
          ) {
            return;
          }

          // POS-owned orders can't be paid through admin ModalPay anyway —
          // the filter is belt-and-braces for the mass-invoice endpoint.
          // Non-invoiceable orders (fully comped or zero total) are excluded
          // so a mixed batch doesn't sweep them into the mass document.
          const eligible = orders.filter(
            (o) =>
              !o.invoicingItemId &&
              o.posDeviceId == null &&
              isOrderInvoiceable(o)
          );
          if (!eligible.length) return;

          const hasNonCash =
            sessionPaymentMethods.some(isAutoInvoicePaymentMethod) ||
            eligible.some(orderHasAutoInvoicePayments);
          if (!hasNonCash) return;

          nextTick(() => {
            void promptAndInvoice(eligible);
          });
        },
      }
    );
  }

  //#region Invoicing (AR fiscal)

  const invoicingEnabled = computed(
    () =>
      restaurantStore.restaurant?.country === 'AR' &&
      getEffectiveTier(restaurantStore.restaurant) > 1
  );

  // posDeviceId == null is REQUIRED: one POS-owned order in the batch makes
  // POST /orders/massInvoice reject the whole request.
  const invoiceableSelected = computed(() =>
    props.selectedOrders.filter(
      (o) =>
        o.paymentStatus === OrderPaymentStatus.Paid &&
        !o.invoicingItemId &&
        o.posDeviceId == null &&
        !invoiceAttempt.isInvoiceLive(o.id) &&
        isOrderInvoiceable(o)
    )
  );

  // promptAndInvoice / invoiceLoading: shared flow in use-mass-invoice.ts
  // (carries the comps-exclusion summary into CreateInvoiceModalArg).

  function invoiceSelected() {
    void promptAndInvoice(invoiceableSelected.value);
  }

  //#endregion

  function massMove() {
    const orders = props.selectedOrders;
    if (!orders.length) return;

    const fromTableId = props.table.id;
    const tables = (restaurantStore.tables ?? []).filter(
      (t) => t.id !== fromTableId
    );

    modals.show(
      SelectModal,
      {
        title: i18nT('posV2.moveTitle', {
          count: orders.length,
          table: props.table.name,
        }),
        message: i18nT('posV2.moveMessage'),
        placeholder: i18nT('posV2.movePlaceholder'),
        items: tables.map((t) => ({
          value: t.id,
          text: t.name,
        })),
      },
      {
        name: 'move',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        submit: async (toTableId: number) => {
          if (toTableId === fromTableId) {
            ui.showToast(AlertType.Error, i18nT('posV2.moveSameTableError'));
            return;
          }
          try {
            await OrdersService.massMove(
              orders.map((o) => o.id),
              toTableId
            );

            ui.showToast(AlertType.Success, i18nT('posV2.movedToast'));
            modals.hide('move');
          } catch (e) {
            ui.serverError(e, i18nT('posV2.moveError'));
          }
        },
      }
    );
  }

  //#endregion
</script>

<template>
  <div class="pos-v2-table-footer">
    <!-- Slim total row (identity lives in the header now) -->
    <div class="footer-info">
      <div class="info-left">
        <PosV2TableDiscountChip
          v-if="discountSession"
          :state="discountState"
          :saving="discountSaving"
          @open="openDiscountModal"
        />
      </div>
      <div class="info-right">
        <span class="total-label">{{ $t('posV2.total') }}</span>
        <span class="total-value">
          {{ money.formatComputedMoney(totalAmount) }}
        </span>
      </div>
    </div>

    <!-- Bulk actions (only with a selection) -->
    <div v-if="selectedOrders.length > 0" class="footer-actions">
      <div class="actions-summary">
        <div class="summary-counts">
          <span class="label-text">
            {{ $t('posV2.selectedCount', { count: selectedOrders.length }) }}
          </span>
          <span class="summary-separator">•</span>
          <span class="summary-total">
            {{ money.formatComputedMoney(selectedTotal) }}
          </span>
        </div>
        <div v-if="selectedRemaining > 0" class="summary-remaining">
          <span class="remaining-label">{{ $t('posV2.remaining') }}:</span>
          <span class="remaining-value">
            {{ money.formatComputedMoney(selectedRemaining) }}
          </span>
        </div>
      </div>

      <div class="actions-buttons">
        <div v-if="deviceCanEverPrint" ref="printMenuRef" class="print-menu">
          <PosUiButton
            color="neutral"
            variant="outline"
            size="lg"
            icon="printer"
            class="action-btn"
            @click="togglePrintMenu"
          >
            <template v-if="!isCompact">
              {{ $t('posV2.print') }}
              <PosUiIcon
                name="chevron-up"
                class="print-chevron"
                :class="{ 'is-open': isPrintMenuOpen }"
              />
            </template>
          </PosUiButton>

          <div
            v-if="isPrintMenuOpen"
            class="print-menu-panel"
            :style="printPanelStyle"
          >
            <button
              v-for="item in printMenuItems"
              :key="item.labelKey"
              type="button"
              class="print-menu-item"
              @click="handlePrint(item)"
            >
              <PosUiIcon :name="item.icon" class="item-icon" />
              <span>{{ $t(item.labelKey) }}</span>
            </button>
          </div>
        </div>

        <PosUiButton
          color="neutral"
          variant="outline"
          size="lg"
          icon="clipboard-text"
          class="action-btn"
          @click="openResume"
        >
          <span v-if="!isCompact">{{ $t('posV2.resume') }}</span>
        </PosUiButton>

        <PosUiButton
          color="neutral"
          variant="outline"
          size="lg"
          icon="table-arrow-right"
          class="action-btn"
          @click="massMove"
        >
          <span v-if="!isCompact">{{ $t('posV2.move') }}</span>
        </PosUiButton>

        <PosUiButton
          color="success"
          variant="soft"
          size="lg"
          icon="check-circle-outline"
          :disabled="allSelectedFinished || settingStatus"
          class="action-btn"
          @click="markDelivered"
        >
          <span v-if="!isCompact">{{ $t('posV2.delivered') }}</span>
        </PosUiButton>

        <PosUiButton
          v-if="invoicingEnabled"
          color="neutral"
          variant="outline"
          size="lg"
          icon="receipt"
          :disabled="invoiceableSelected.length === 0 || invoiceLoading"
          class="action-btn"
          @click="invoiceSelected"
        >
          <span v-if="!isCompact">{{ $t('posV2.invoice') }}</span>
        </PosUiButton>

        <PosUiButton
          color="primary"
          variant="solid"
          size="lg"
          icon="wallet"
          :disabled="allSelectedPaid"
          class="action-btn"
          @click="doPayment"
        >
          <span v-if="!isCompact">{{ $t('posV2.pay') }}</span>
        </PosUiButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-table-footer {
    @apply tw-sticky tw-bottom-0 tw-z-10 tw-flex-none tw-border-t tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-2;

    .footer-info {
      @apply tw-flex tw-items-center tw-justify-between;

      .info-left {
        @apply tw-flex tw-items-center;
      }

      .info-right {
        @apply tw-flex tw-items-baseline tw-gap-2;

        .total-label {
          @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-400;
        }

        .total-value {
          @apply tw-text-lg tw-font-bold tw-tabular-nums tw-text-neutral-900;
        }
      }
    }

    .footer-actions {
      @apply tw-mt-2 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-neutral-100 tw-pt-2 max-md:tw-flex-col max-md:tw-items-stretch max-md:tw-gap-2;

      .actions-summary {
        @apply tw-flex tw-flex-col tw-gap-0.5;

        .summary-counts {
          @apply tw-flex tw-items-center tw-gap-2 tw-text-sm;

          .label-text {
            @apply tw-font-medium tw-text-neutral-600;
          }

          .summary-separator {
            @apply tw-text-neutral-300;
          }

          .summary-total {
            @apply tw-font-bold tw-tabular-nums tw-text-neutral-900;
          }
        }

        .summary-remaining {
          @apply tw-flex tw-items-center tw-gap-1.5 tw-text-xs;

          .remaining-label {
            @apply tw-text-neutral-500;
          }

          .remaining-value {
            @apply tw-font-semibold tw-tabular-nums tw-text-amber-600;
          }
        }
      }

      .actions-buttons {
        @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 max-md:tw-justify-end;

        .action-btn {
          @apply tw-whitespace-nowrap;
        }
      }
    }

    .print-menu {
      @apply tw-relative;

      .print-chevron {
        @apply tw-ml-1 tw-text-base tw-transition-transform tw-duration-150;

        &.is-open {
          @apply tw-rotate-180;
        }
      }

      .print-menu-panel {
        @apply tw-fixed tw-z-50 tw-min-w-[190px] tw-overflow-hidden tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-p-1 tw-shadow-lg;

        .print-menu-item {
          @apply tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-md tw-px-3 tw-py-2.5 tw-text-left tw-text-sm tw-font-medium tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-100;

          .item-icon {
            @apply tw-text-base tw-text-neutral-500;
          }
        }
      }
    }
  }
</style>
